import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import AdjusterQABot from '../components/AdjusterQABot';
import { 
  adjusterAnalyzerApi, 
  AdjusterAnalysisResult, 
  AdjusterClaimItem,
  DocumentationGap,
  CausationAssessment,
  ReductionArgument
} from '../services/adjusterAnalyzerApi';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingDown,
  TrendingUp,
  FileText,
  RefreshCw,
  DollarSign,
  ShieldAlert,
  Scale,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Info,
  Clock,
  BarChart3,
  PieChart,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Assessment colors
const ASSESSMENT_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  'SUPPORTED': { label: 'Supported', color: 'text-green-700', bgColor: 'bg-green-100' },
  'PARTIALLY_SUPPORTED': { label: 'Partial', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  'QUESTIONED': { label: 'Questioned', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  'DENIED': { label: 'Denied', color: 'text-red-700', bgColor: 'bg-red-100' }
};

// Risk colors
const RISK_CONFIG: Record<string, { color: string; bgColor: string }> = {
  'LOW': { color: 'text-green-700', bgColor: 'bg-green-100' },
  'MEDIUM': { color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  'HIGH': { color: 'text-red-700', bgColor: 'bg-red-100' }
};

// Causation colors
const CAUSATION_CONFIG: Record<string, { label: string; color: string }> = {
  'CLEAR': { label: 'Clear', color: 'text-green-600' },
  'PROBABLE': { label: 'Probable', color: 'text-blue-600' },
  'UNCERTAIN': { label: 'Uncertain', color: 'text-yellow-600' },
  'UNLIKELY': { label: 'Unlikely', color: 'text-orange-600' },
  'UNRELATED': { label: 'Unrelated', color: 'text-red-600' }
};

// Gap category colors
const GAP_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  'CRITICAL': { label: 'Critical', color: 'text-red-700', bgColor: 'bg-red-100' },
  'MAJOR': { label: 'Major', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  'MINOR': { label: 'Minor', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  'CLARIFICATION': { label: 'Clarification', color: 'text-blue-700', bgColor: 'bg-blue-100' }
};

export default function AdjusterAnalyzerView() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<AdjusterAnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  
  // Expandable sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['summary', 'claim_items'])
  );
  
  // QA Bot sidebar state
  const [qaBotOpen, setQaBotOpen] = useState(false);

  useEffect(() => {
    if (reportId) {
      fetchAnalysis();
    }
  }, [reportId]);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adjusterAnalyzerApi.getAnalysis(reportId!);
      setAnalysis(data);
    } catch (err: any) {
      console.error('Failed to fetch analysis:', err);
      if (err.message.includes('not found')) {
        setError('No analysis found for this report. Click "Run Analysis" to generate one.');
      } else {
        setError(err.message || 'Failed to load analysis');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRunAnalysis = async (forceRefresh: boolean = false) => {
    if (!reportId) return;
    
    try {
      setRunning(true);
      const result = await adjusterAnalyzerApi.runAnalysis(reportId, forceRefresh);
      setAnalysis(result.result);
      setError(null);
      toast.success('Analysis completed successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to run analysis');
    } finally {
      setRunning(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-brand-primary" />
          <span className="ml-2 text-gray-600">Loading analysis...</span>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="adjuster-analyzer-view">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate(`/report/${reportId}`)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
              data-testid="back-to-report-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Report
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Adjuster Analysis</h1>
            <p className="text-sm text-gray-500 mt-1">Conservative insurance adjuster perspective on this claim</p>
          </div>
          
          <div className="flex gap-3">
            {analysis && (
              <button
                onClick={() => handleRunAnalysis(true)}
                disabled={running}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="refresh-analysis-btn"
              >
                <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
                Re-run Analysis
              </button>
            )}
            {!analysis && (
              <button
                onClick={() => handleRunAnalysis(false)}
                disabled={running}
                className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-colors"
                data-testid="run-analysis-btn"
              >
                {running ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <BarChart3 className="w-4 h-4" />
                )}
                Run Analysis
              </button>
            )}
          </div>
        </div>

        {/* Error State */}
        {error && !analysis && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
            <h2 className="text-lg font-medium text-yellow-900 mb-2">Analysis Not Available</h2>
            <p className="text-yellow-700 mb-4">{error}</p>
            <button
              onClick={() => handleRunAnalysis(false)}
              disabled={running}
              className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-colors"
            >
              {running ? 'Running...' : 'Run Analysis Now'}
            </button>
          </div>
        )}

        {/* Analysis Content */}
        {analysis && (
          <div className="space-y-6">
            {/* Executive Summary */}
            <CollapsibleSection
              title="Executive Summary"
              icon={PieChart}
              expanded={expandedSections.has('summary')}
              onToggle={() => toggleSection('summary')}
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <SummaryCard
                  label="Supported"
                  value={formatCurrency(analysis.executive_summary.supported_total)}
                  color="text-green-700"
                  bgColor="bg-green-50"
                  icon={CheckCircle}
                />
                <SummaryCard
                  label="Questioned"
                  value={formatCurrency(analysis.executive_summary.questioned_total)}
                  color="text-yellow-700"
                  bgColor="bg-yellow-50"
                  icon={AlertTriangle}
                />
                <SummaryCard
                  label="Denied"
                  value={formatCurrency(analysis.executive_summary.denied_total)}
                  color="text-red-700"
                  bgColor="bg-red-50"
                  icon={XCircle}
                />
                <SummaryCard
                  label="Attorney Claimed"
                  value={formatCurrency(analysis.executive_summary.attorney_claimed_total)}
                  color="text-blue-700"
                  bgColor="bg-blue-50"
                  icon={Scale}
                />
              </div>
              
              {/* Variance */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Variance from Attorney Position</span>
                  <span className={`text-lg font-bold ${
                    analysis.executive_summary.variance_from_attorney < 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {analysis.executive_summary.variance_from_attorney < 0 ? '' : '+'}
                    {formatCurrency(analysis.executive_summary.variance_from_attorney)}
                  </span>
                </div>
              </div>

              {/* Key Reduction Factors */}
              {analysis.executive_summary.key_reduction_factors?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Key Reduction Factors</h4>
                  <ul className="space-y-1">
                    {analysis.executive_summary.key_reduction_factors.map((factor, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <TrendingDown className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CollapsibleSection>

            {/* Risk Assessment */}
            <CollapsibleSection
              title="Risk Assessment"
              icon={ShieldAlert}
              expanded={expandedSections.has('risk')}
              onToggle={() => toggleSection('risk')}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <RiskCard
                  label="Underpayment Risk"
                  level={analysis.risk_assessment.underpayment_risk}
                  description="Risk of paying less than actually owed"
                />
                <RiskCard
                  label="Litigation Risk"
                  level={analysis.risk_assessment.litigation_risk}
                  description="Risk of legal action from insured"
                />
                <RiskCard
                  label="Bad Faith Risk"
                  level={analysis.risk_assessment.bad_faith_risk}
                  description="Risk of bad faith claim"
                />
              </div>
              {analysis.risk_assessment.risk_notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700">{analysis.risk_assessment.risk_notes}</p>
                </div>
              )}
            </CollapsibleSection>

            {/* Claim Items */}
            <CollapsibleSection
              title={`Claim Items (${analysis.claim_items.length})`}
              icon={FileText}
              expanded={expandedSections.has('claim_items')}
              onToggle={() => toggleSection('claim_items')}
            >
              {analysis.claim_items.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No claim items analyzed</p>
              ) : (
                <div className="space-y-3">
                  {analysis.claim_items.map((item) => (
                    <ClaimItemCard key={item.id} item={item} />
                  ))}
                </div>
              )}
            </CollapsibleSection>

            {/* Documentation Gaps */}
            <CollapsibleSection
              title={`Documentation Gaps (${analysis.documentation_analysis.gaps?.length || 0})`}
              icon={AlertTriangle}
              expanded={expandedSections.has('gaps')}
              onToggle={() => toggleSection('gaps')}
              badge={analysis.documentation_analysis.gaps?.filter(g => g.category === 'CRITICAL').length || 0}
              badgeColor="bg-red-100 text-red-700"
            >
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-gray-600">Overall Completeness:</span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-brand-primary rounded-full transition-all"
                      style={{ width: `${analysis.documentation_analysis.overall_completeness}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{analysis.documentation_analysis.overall_completeness}%</span>
                </div>
              </div>
              
              {(!analysis.documentation_analysis.gaps || analysis.documentation_analysis.gaps.length === 0) ? (
                <p className="text-green-600 text-center py-4 flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  No documentation gaps identified
                </p>
              ) : (
                <div className="space-y-3">
                  {analysis.documentation_analysis.gaps.map((gap) => (
                    <GapCard key={gap.id} gap={gap} />
                  ))}
                </div>
              )}
            </CollapsibleSection>

            {/* Causation Analysis */}
            <CollapsibleSection
              title="Causation Analysis"
              icon={TrendingUp}
              expanded={expandedSections.has('causation')}
              onToggle={() => toggleSection('causation')}
            >
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Overall Causation Strength:</span>
                  <span className={`font-medium ${CAUSATION_CONFIG[analysis.causation_analysis.overall_causation_strength]?.color || 'text-gray-600'}`}>
                    {CAUSATION_CONFIG[analysis.causation_analysis.overall_causation_strength]?.label || analysis.causation_analysis.overall_causation_strength}
                  </span>
                </div>
              </div>
              
              {analysis.causation_analysis.by_category?.length > 0 && (
                <div className="space-y-3">
                  {analysis.causation_analysis.by_category.map((assessment, idx) => (
                    <CausationCard key={idx} assessment={assessment} />
                  ))}
                </div>
              )}
            </CollapsibleSection>

            {/* Conservative P&L */}
            <CollapsibleSection
              title="Conservative P&L"
              icon={DollarSign}
              expanded={expandedSections.has('pnl')}
              onToggle={() => toggleSection('pnl')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Baseline Period</h4>
                  <p className="text-sm text-gray-500 mb-3">{analysis.conservative_pnl.baseline_period}</p>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Revenue</dt>
                      <dd className="text-sm font-medium">{formatCurrency(analysis.conservative_pnl.baseline_revenue)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Expenses</dt>
                      <dd className="text-sm font-medium">{formatCurrency(analysis.conservative_pnl.baseline_expenses)}</dd>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <dt className="text-sm font-medium text-gray-900">Net Income</dt>
                      <dd className="text-sm font-bold">{formatCurrency(analysis.conservative_pnl.baseline_net_income)}</dd>
                    </div>
                  </dl>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Projected (But-For)</h4>
                  <p className="text-sm text-gray-500 mb-3">Growth Rate Applied: {formatPercent(analysis.conservative_pnl.growth_rate_applied)}</p>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Revenue</dt>
                      <dd className="text-sm font-medium">{formatCurrency(analysis.conservative_pnl.projected_revenue)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Expenses</dt>
                      <dd className="text-sm font-medium">{formatCurrency(analysis.conservative_pnl.projected_expenses)}</dd>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <dt className="text-sm font-medium text-gray-900">Net Income</dt>
                      <dd className="text-sm font-bold">{formatCurrency(analysis.conservative_pnl.projected_net_income)}</dd>
                    </div>
                  </dl>
                </div>
              </div>
              
              <div className="mt-6 bg-brand-primary/10 rounded-lg p-4 text-center">
                <span className="text-sm text-gray-600">Calculated Loss</span>
                <div className="text-2xl font-bold text-brand-primary">
                  {formatCurrency(analysis.conservative_pnl.calculated_loss)}
                </div>
              </div>

              {analysis.conservative_pnl.methodology_notes && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Methodology Notes</h5>
                  <p className="text-sm text-gray-600">{analysis.conservative_pnl.methodology_notes}</p>
                </div>
              )}
            </CollapsibleSection>

            {/* Reduction Arguments */}
            <CollapsibleSection
              title={`Reduction Arguments (${analysis.reduction_arguments.length})`}
              icon={TrendingDown}
              expanded={expandedSections.has('reduction')}
              onToggle={() => toggleSection('reduction')}
            >
              {analysis.reduction_arguments.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No reduction arguments applied</p>
              ) : (
                <div className="space-y-3">
                  {analysis.reduction_arguments.map((arg) => (
                    <ReductionArgumentCard key={arg.id} argument={arg} />
                  ))}
                </div>
              )}
            </CollapsibleSection>

            {/* Metadata */}
            <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-500">
              <div className="flex flex-wrap gap-4">
                <span>Analysis ID: {analysis.id}</span>
                <span>•</span>
                <span>Model: {analysis.metadata.ai_model_used}</span>
                <span>•</span>
                <span>Processing Time: {analysis.metadata.processing_time_seconds}s</span>
                <span>•</span>
                <span>Created: {new Date(analysis.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* AI QA Bot Sidebar */}
      {reportId && (
        <>
          <AdjusterQABot
            reportId={reportId}
            isOpen={qaBotOpen}
            onToggle={() => setQaBotOpen(!qaBotOpen)}
          />
          
          {/* QA Bot toggle button when closed and has analysis */}
          {!qaBotOpen && analysis && (
            <button
              onClick={() => setQaBotOpen(true)}
              className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all z-30"
              title="Ask AI about this analysis"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Ask AI</span>
            </button>
          )}
        </>
      )}
    </AppLayout>
  );
}

// Collapsible Section Component
function CollapsibleSection({ 
  title, 
  icon: Icon, 
  expanded, 
  onToggle, 
  children,
  badge,
  badgeColor = 'bg-gray-100 text-gray-700'
}: { 
  title: string; 
  icon: React.ElementType; 
  expanded: boolean; 
  onToggle: () => void;
  children: React.ReactNode;
  badge?: number;
  badgeColor?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-brand-primary" />
          <span className="font-semibold text-gray-900">{title}</span>
          {badge !== undefined && badge > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
              {badge} critical
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {expanded && (
        <div className="px-6 pb-6 border-t border-gray-100 pt-4">
          {children}
        </div>
      )}
    </div>
  );
}

// Summary Card
function SummaryCard({ label, value, color, bgColor, icon: Icon }: { 
  label: string; 
  value: string; 
  color: string; 
  bgColor: string;
  icon: React.ElementType;
}) {
  return (
    <div className={`${bgColor} rounded-lg p-4`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs font-medium text-gray-600">{label}</span>
      </div>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

// Risk Card
function RiskCard({ label, level, description }: { label: string; level: string; description: string }) {
  const config = RISK_CONFIG[level] || RISK_CONFIG['LOW'];
  return (
    <div className={`${config.bgColor} rounded-lg p-4`}>
      <h4 className="text-sm font-medium text-gray-700 mb-1">{label}</h4>
      <div className={`text-lg font-bold ${config.color}`}>{level}</div>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </div>
  );
}

// Claim Item Card
function ClaimItemCard({ item }: { item: AdjusterClaimItem }) {
  const assessmentConfig = ASSESSMENT_CONFIG[item.assessment] || ASSESSMENT_CONFIG['QUESTIONED'];
  const causationConfig = CAUSATION_CONFIG[item.causation_rating] || CAUSATION_CONFIG['UNCERTAIN'];
  
  const variance = item.claimed_amount - item.supported_amount;
  const variancePercent = item.claimed_amount > 0 ? (variance / item.claimed_amount) * 100 : 0;
  
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <span className="text-xs text-gray-500">{item.category}</span>
          <h4 className="font-medium text-gray-900">{item.description}</h4>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${assessmentConfig.bgColor} ${assessmentConfig.color}`}>
          {assessmentConfig.label}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-3">
        <div>
          <span className="text-xs text-gray-500">Claimed</span>
          <div className="font-semibold text-gray-900">
            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(item.claimed_amount)}
          </div>
        </div>
        <div>
          <span className="text-xs text-gray-500">Supported</span>
          <div className="font-semibold text-green-700">
            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(item.supported_amount)}
          </div>
        </div>
        <div>
          <span className="text-xs text-gray-500">Variance</span>
          <div className={`font-semibold ${variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
            -{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(variance)}
            <span className="text-xs ml-1">({variancePercent.toFixed(0)}%)</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
        <span>Docs: <span className={item.documentation_status === 'COMPLETE' ? 'text-green-600' : 'text-yellow-600'}>{item.documentation_status}</span></span>
        <span>•</span>
        <span>Causation: <span className={causationConfig.color}>{causationConfig.label}</span></span>
      </div>
      
      {item.rationale && (
        <p className="text-sm text-gray-600 bg-gray-50 rounded p-2">{item.rationale}</p>
      )}
    </div>
  );
}

// Gap Card
function GapCard({ gap }: { gap: DocumentationGap }) {
  const config = GAP_CONFIG[gap.category] || GAP_CONFIG['MINOR'];
  
  return (
    <div className={`border-l-4 rounded-lg p-4 bg-white border ${
      gap.category === 'CRITICAL' ? 'border-l-red-500' :
      gap.category === 'MAJOR' ? 'border-l-orange-500' :
      gap.category === 'MINOR' ? 'border-l-yellow-500' : 'border-l-blue-500'
    }`}>
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-gray-900">{gap.description}</h4>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.color}`}>
          {config.label}
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-2">
        <span className="font-medium">How to resolve:</span> {gap.remediation}
      </p>
      {gap.impact_estimate > 0 && (
        <p className="text-sm text-orange-600">
          Estimated impact: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(gap.impact_estimate)}
        </p>
      )}
    </div>
  );
}

// Causation Card
function CausationCard({ assessment }: { assessment: CausationAssessment }) {
  const config = CAUSATION_CONFIG[assessment.rating] || CAUSATION_CONFIG['UNCERTAIN'];
  
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-900">{assessment.category}</h4>
        <span className={`font-medium ${config.color}`}>{config.label}</span>
      </div>
      <p className="text-sm text-gray-600 mb-2">{assessment.rationale}</p>
      {assessment.alternative_explanations?.length > 0 && (
        <div className="text-xs text-gray-500">
          <span className="font-medium">Alternative explanations: </span>
          {assessment.alternative_explanations.join(', ')}
        </div>
      )}
      {assessment.adjustment_factor !== 1.0 && (
        <div className="text-xs text-orange-600 mt-1">
          Adjustment factor: {(assessment.adjustment_factor * 100).toFixed(0)}%
        </div>
      )}
    </div>
  );
}

// Reduction Argument Card
function ReductionArgumentCard({ argument }: { argument: ReductionArgument }) {
  const strengthColors = {
    'STRONG': 'text-red-600 bg-red-100',
    'MODERATE': 'text-orange-600 bg-orange-100',
    'WEAK': 'text-yellow-600 bg-yellow-100'
  };
  
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <span className="text-xs text-gray-500">{argument.argument_type}</span>
          <h4 className="font-medium text-gray-900">{argument.description}</h4>
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${strengthColors[argument.strength] || strengthColors['MODERATE']}`}>
          {argument.strength}
        </span>
      </div>
      {argument.dollar_impact > 0 && (
        <p className="text-sm text-red-600 mb-2">
          Impact: -{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(argument.dollar_impact)}
        </p>
      )}
      <p className="text-xs text-gray-500">
        <span className="font-medium">Counter-evidence needed:</span> {argument.counter_evidence_needed}
      </p>
    </div>
  );
}
